import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import {
  MuiBox,
  MuiContainer,
  MuiDivider,
  MuiFormControl,
  MuiGrid,
  MuiInputLabel,
  MuiMenuItem,
  MuiSelect,
  MuiTypography
} from 'theme/material-ui';

import { Svg } from 'components/UI/Svg';
import { LazyMuiAvatar } from 'components/LazyAvatar';
import DataLoader from 'components/UI/DataLoader/DataLoader';

import { Color } from 'modules/styles/colors';
import { FontSize, IconSize } from 'modules/styles/variables';
import { providerSelectConstant } from 'modules/constants/booking';

import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';

import * as bookingTypes from 'store/booking/types';
import * as bookingActions from 'store/booking/actions';
import * as findProviderActions from 'store/findProvider/actions';
import { mergeFiltersAndFacets } from 'store/findProvider/utils';
import * as findProviderConstants from 'store/findProvider/constants';
import { ProviderSearchFilters, ProviderSummary } from 'store/findProvider/types';

import { onEnter } from 'utils/keyboard';

import { useCheckBookingInfoEffect } from '../useCheckBookingInfoEffect';
import { BookingPanel, OrBox, SelectHealthIconWrapper, StyledScreen } from '../components/styled';
import {
  BookingLoading,
  BookingNoData,
  BookingMoreInfoButton
} from '../components/sharedComponents';
import SpecialtyOrMyDoctorSelectMoreInfoDialog from '../dialogs/SpecialtyOrMyDoctorSelectMoreInfoDialog';
import { convertToLowerKabobCase } from 'modules/utils/StringUtils';

import Spinner from 'components/UI/Spinner/Spinner';
import { getMyDoctors } from 'shared/src/store/myDoctors/actions';

export interface Props extends RouteComponentProps {
  appointmentDetails: bookingTypes.AppointmentDetails;
  doctorsLoading: boolean;
  doctors: ProviderSummary[];
  geolocation: Coordinates;
  geolocationStatus: boolean;
  getSpecialties: typeof bookingActions.getSpecialties;
  getMyDoctors: typeof getMyDoctors;
  setActiveProviderSearchFilter: typeof findProviderActions.setActiveProviderSearchFilter;
  setDoctor: typeof bookingActions.setDoctor;
  setSpecialty: typeof bookingActions.setSpecialty;
  specialties: bookingTypes.Specialty[];
  specialtiesLoading: boolean;
  setSort: typeof findProviderActions.setSortBy;
  providerSearch: typeof findProviderActions.schedulingProviderSearch;
  currentRouteName?: string;
  previousRouteName?: string;
}

export function BookingSpecialtyOrMyDoctorSelectDefault({
  appointmentDetails,
  doctors,
  doctorsLoading,
  geolocationStatus,
  getMyDoctors,
  getSpecialties,
  setActiveProviderSearchFilter,
  setDoctor,
  setSpecialty,
  specialties,
  specialtiesLoading,
  setSort,
  providerSearch,
  history,
  currentRouteName,
  previousRouteName
}: Props) {
  const eventData: AmplitudeEventData = {
    currentUrl: currentRouteName,
    referringUrl: previousRouteName
  };

  const persistRef = useRef(false);

  const [isMoreInfoDialogOpen, setIsMoreInfoDialogOpen] = useState(false);
  // const [selectedSpecialty, setSelectedSpecialty] = useState<SpecialtyStub | null>(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState<bookingTypes.Specialty | null>(null);

  const isLoading = specialtiesLoading || doctorsLoading;

  useEffect(() => {
    getSpecialties();
    getMyDoctors();
  }, []);

  useCheckBookingInfoEffect(appointmentDetails?.patient);

  const handleMoreInfoOpen = () => {
    const helpUsedData: AmplitudeEventData = {
      ...eventData,
      /* eslint-disable-next-line @typescript-eslint/camelcase */
      source_page: 'SelectProviderPage'
    };
    analyticsService.logEvent(AnalyticsEvent.BookingHelpUsed, helpUsedData);
    setIsMoreInfoDialogOpen(true);
  };

  const handleSpecialtySelect = (
    event: React.ChangeEvent<{ value: bookingTypes.Specialty | null }>
  ) => {
    setSpecialty(event.target.value);
    setSelectedSpecialty(event.target.value);

    const filters = { isAcceptingOnlineBooking: true };
    if (event.target.value?.nameShort !== 'OB|OBG|GYN') {
      filters.acceptingNewPatients = true;
    }

    const facets = {
      specialty: [
        {
          name: event.target.value?.nameLong || '',
          group: 'specialty',
          value: event.target.value?.nameLong || ''
        }
      ]
    };

    const dispatchSearchFilters: ProviderSearchFilters = mergeFiltersAndFacets(filters, facets);

    // sets filters and facets
    setActiveProviderSearchFilter(dispatchSearchFilters);
    // sets sort order
    setSort(findProviderConstants.SORT_BY_PROXIMITY);
    // fires off search
    providerSearch();

    persistRef.current = true;

    history.push('/u/get-care-now/booking/specialty-provider-select');
  };

  const handleProviderSelect = (provider: ProviderSummary) => {
    setDoctor(provider);
    history.push('/u/get-care-now/booking/visit-type-select');

    analyticsService.logEvent(AnalyticsEvent.BookingProviderSelected, eventData);
  };

  const getProviderIsSelected = (provider: ProviderSummary) => {
    if (!provider || !appointmentDetails.doctor) {
      return false;
    }

    return appointmentDetails.doctor.corpProvId === provider.corpProvId;
  };

  return (
    <>
      <StyledScreen>
        <MuiContainer maxWidth="lg">
          <MuiBox my={3}>
            <MuiGrid container>
              <MuiGrid item xs>
                <MuiTypography gutterBottom variant="h4">
                  Schedule with a new provider
                </MuiTypography>
              </MuiGrid>
              <MuiGrid item>
                <BookingMoreInfoButton
                  testID="booking-specialty-or-my-doctor-select-more-info-button"
                  handleOpen={() => handleMoreInfoOpen()}
                />
              </MuiGrid>
            </MuiGrid>
            <MuiBox mb={2}>
              <MuiDivider />
            </MuiBox>
            <MuiBox width="30%" my={4}>
              <MuiFormControl fullWidth variant="outlined">
                <MuiInputLabel id="booking-provider-specialty-select-label">
                  Available Specialties
                </MuiInputLabel>
                <MuiSelect
                  labelId="booking-provider-specialty-select-label"
                  id="booking-provider-specialty-select"
                  data-testid="booking-provider-specialty-select"
                  value={selectedSpecialty}
                  onChange={handleSpecialtySelect}
                  label="Available Specialties"
                >
                  {specialties.map(specialty => (
                    <MuiMenuItem
                      key={specialty.id}
                      value={specialty}
                      data-testid={convertToLowerKabobCase(specialty.nameLong)}
                    >
                      {specialty?.moreInfo?.title ? specialty?.moreInfo?.title : specialty.nameLong}
                    </MuiMenuItem>
                  ))}
                </MuiSelect>
              </MuiFormControl>
            </MuiBox>
            {doctors.length || doctorsLoading ? (
              <>
                <MuiBox textAlign="center" my={5}>
                  <MuiDivider />

                  <OrBox>
                    <MuiTypography>OR</MuiTypography>
                  </OrBox>
                </MuiBox>

                <MuiBox>
                  <MuiTypography variant="h4">Schedule with your care team</MuiTypography>
                </MuiBox>
                <MuiBox py={2}>
                  <DataLoader
                    data={doctors}
                    loading={isLoading}
                    renderLoading={() => (
                      <MuiBox textAlign="center" paddingTop={3}>
                        <Spinner
                          size={IconSize.xLarge}
                          data-testid="booking-specialty-or-my-doctor-select-loading"
                        />
                        <BookingLoading />
                      </MuiBox>
                    )}
                    renderNoData={() => (
                      <BookingNoData
                        message={providerSelectConstant.GET_MY_DOCTORS.NO_DATA.message}
                      />
                    )}
                    renderData={() => (
                      <MuiBox role="radiogroup">
                        {doctors.map(doctor => (
                          <BookingPanel
                            tabindex="0"
                            role="radio"
                            elevation={2}
                            key={doctor.corpProvId}
                            aria-checked={getProviderIsSelected(doctor)}
                            selected={getProviderIsSelected(doctor)}
                            onClick={() => handleProviderSelect(doctor)}
                            onKeyUp={onEnter(() => handleProviderSelect(doctor))}
                            data-testid={convertToLowerKabobCase(doctor.displayName)}
                          >
                            <MuiGrid container spacing={2} alignItems="center">
                              <MuiGrid item>
                                <MuiBox mr={2}>
                                  <LazyMuiAvatar
                                    src={doctor?.providerProfileImageUrl}
                                    style={{ width: IconSize.large, height: IconSize.large }}
                                  />
                                  {doctor?.isSelectHealth ? (
                                    <SelectHealthIconWrapper>
                                      <Svg set="assets" name="SelectHealth" size={IconSize.small} />
                                    </SelectHealthIconWrapper>
                                  ) : null}
                                </MuiBox>
                              </MuiGrid>
                              <MuiGrid container item sm xs={12} alignItems="center">
                                <MuiGrid item xs container direction="column" spacing={2}>
                                  <MuiGrid item xs>
                                    <MuiTypography data-testid="provider-name">
                                      {doctor?.displayName}
                                    </MuiTypography>
                                    <MuiTypography
                                      variant="subtitle1"
                                      data-testid="provider-specialty"
                                    >
                                      {doctor?.primarySpecialty?.specialtyName || ''}
                                    </MuiTypography>
                                  </MuiGrid>
                                </MuiGrid>
                                {doctor?.offersVideoVisits ? (
                                  <MuiGrid container direction="row" alignItems="center" item xs>
                                    <Svg set="assets" name="VideoVisit" />
                                    <MuiBox ml={0.5}>
                                      <MuiTypography
                                        data-testid="provider-primary-has-video-visits-available"
                                        fontSize={FontSize.small}
                                      >
                                        Video visits available
                                      </MuiTypography>
                                    </MuiBox>
                                  </MuiGrid>
                                ) : (
                                  <MuiGrid item xs />
                                )}
                                <MuiGrid item>
                                  <MuiBox mr={0.5}>
                                    <Svg set="material" name="location-on" size={IconSize.base} />
                                  </MuiBox>
                                </MuiGrid>
                                <MuiGrid container item direction="column" xs>
                                  <MuiTypography
                                    fontSize={FontSize.small}
                                    data-testid="provider-primary-distance"
                                  >
                                    {geolocationStatus ? doctor?.distance : null}
                                  </MuiTypography>
                                  <MuiTypography
                                    fontSize={FontSize.small}
                                    data-testid="provider-primary-location"
                                  >
                                    {doctor?.primaryLocation?.name}
                                  </MuiTypography>
                                </MuiGrid>
                                <MuiGrid item>
                                  <Svg color={Color.black} set="material" name="chevron-right" />
                                </MuiGrid>
                              </MuiGrid>
                            </MuiGrid>
                          </BookingPanel>
                        ))}
                      </MuiBox>
                    )}
                  />
                </MuiBox>
              </>
            ) : null}
          </MuiBox>
        </MuiContainer>
      </StyledScreen>

      <SpecialtyOrMyDoctorSelectMoreInfoDialog
        isOpen={isMoreInfoDialogOpen}
        handleClose={() => setIsMoreInfoDialogOpen(false)}
      />
    </>
  );
}

export default BookingSpecialtyOrMyDoctorSelectDefault;
